import React, { useState, useEffect } from "react";
import Sidebar from "../Sidebar/Sidebar";
import Navbar from "../Navbar/Navbar";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Button } from "primereact/button";
import { Dialog } from "primereact/dialog";
import { InputText } from "primereact/inputtext";
// import { MultiSelect } from "primereact/multiselect";
// import { Dropdown } from "primereact/dropdown";
import { Password } from "primereact/password";
import MultiSelectDropdown from "../components/MultiSelectDropdown";
import { axiosConfig } from "../axiosAPI";
import Swal from "sweetalert2";
import { useAuth } from "../AuthProvider";
import PermissionGate from "../components/PermissionGate";
import "../Assets/Css/FormImprovements.css";

function SubAdmins() {
  const [subAdmins, setSubAdmins] = useState([]);
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [visible, setVisible] = useState(false);
  const [passwordDialogVisible, setPasswordDialogVisible] = useState(false);
  const [rolesDialogVisible, setRolesDialogVisible] = useState(false);
  const [globalFilterValue, setGlobalFilterValue] = useState("");

  const [formData, setFormData] = useState({
    uuid: null,
    fullName: "",
    phoneNumber: "",
    password: "",
    confirmPassword: "",
    state: "Enabled",
    roles: [],
  });

  const [passwordData, setPasswordData] = useState({
    uuid: null,
    newPassword: "",
    confirmNewPassword: "",
  });

  const [rolesData, setRolesData] = useState({
    uuid: null,
    roles: [],
  });

  // Fetch sub-admins
  const getSubAdmins = async () => {
    setLoading(true);
    try {
      const response = await axiosConfig.get(
        `${process.env.REACT_APP_BASE_URL_V1}/sub-admins`,
        {
          params: { pageNo: 1, rowCount: 100 },
        }
      );
      console.log(process.env.REACT_APP_BASE_URL_V1);
      setSubAdmins(response.data.result);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch sub-admins",
      });
    } finally {
      setLoading(false);
    }
  };

  // Fetch available roles
  const getRoles = async () => {
    try {
      const response = await axiosConfig.get(
        `${process.env.REACT_APP_BASE_URL}/roles`,
        {
          params: { status: "Active" },
        }
      );
      setRoles(response.data.result);
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to fetch roles",
      });
    }
  };

  // Create sub-admin
  const createSubAdmin = async () => {
    // Basic validation
    if (!formData.fullName.trim()) {
      Swal.fire({ icon: "error", title: "Error", text: "Full name is required" });
      return;
    }
    if (!formData.phoneNumber.trim()) {
      Swal.fire({ icon: "error", title: "Error", text: "Phone number is required" });
      return;
    }
    if (!formData.password.trim()) {
      Swal.fire({ icon: "error", title: "Error", text: "Password is required" });
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Passwords do not match",
      });
      return;
    }

    try {
      await axiosConfig.post(`${process.env.REACT_APP_BASE_URL_V1}/sub-admins`, {
        fullName: formData.fullName,
        phoneNumber: formData.phoneNumber,
        password: formData.password,
        confirmPassword: formData.confirmPassword,
        state: formData.state,
        roles: formData.roles || [],
      });
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Sub-admin created successfully",
      });
      getSubAdmins();
      setVisible(false);
      resetForm();
    } catch (error) {
      console.error("Create sub-admin error:", error);
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to create sub-admin",
      });
    }
  };

  // Update sub-admin roles - send array of role identifiers only
  const updateSubAdminRoles = async () => {
    try {
      // Ensure all values are identifiers (case/whitespace tolerant)
      const normalize = (val) => (typeof val === 'string' ? val.trim() : val);
      const identifiers = (rolesData.roles || []).map((value) => {
        const v = normalize(value);
        const match = roles.find((r) => {
          const id = normalize(r.identifier);
          const en = normalize(r.displayName_En);
          const ar = normalize(r.displayName_Ar);
          return (
            id === v ||
            (typeof v === 'string' && (en?.toLowerCase() === v.toLowerCase() || ar?.toLowerCase() === v.toLowerCase()))
          );
        });
        return match?.identifier || v;
      });

      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL_V1}/sub-admins/${rolesData.uuid}/roles`,
        identifiers
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Roles updated successfully",
      });
      getSubAdmins();
      setRolesDialogVisible(false);
      setRolesData({ uuid: null, roles: [] });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to update roles",
      });
    }
  };

  // Change password
  const changePassword = async () => {
    if (passwordData.newPassword !== passwordData.confirmNewPassword) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Passwords do not match",
      });
      return;
    }

    try {
      await axiosConfig.put(
        `${process.env.REACT_APP_BASE_URL_V1}/sub-admins/${passwordData.uuid}/password`,
        {
          newPassword: passwordData.newPassword,
          confirmNewPassword: passwordData.confirmNewPassword,
        }
      );
      Swal.fire({
        icon: "success",
        title: "Success",
        text: "Password changed successfully",
      });
      setPasswordDialogVisible(false);
      setPasswordData({ uuid: null, newPassword: "", confirmNewPassword: "" });
    } catch (error) {
      Swal.fire({
        icon: "error",
        title: "Error",
        text: "Failed to change password",
      });
    }
  };

  // Delete sub-admin
  const deleteSubAdmin = async (uuid) => {
    const result = await Swal.fire({
      title: "Are you sure?",
      text: "This action cannot be undone",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#E23904",
      cancelButtonColor: "#6c757d",
      confirmButtonText: "Yes, delete it!",
    });

    if (result.isConfirmed) {
      try {
        await axiosConfig.delete(
          `${process.env.REACT_APP_BASE_URL_V1}/sub-admins/${uuid}`
        );
        Swal.fire({
          icon: "success",
          title: "Deleted",
          text: "Sub-admin deleted successfully",
        });
        getSubAdmins();
      } catch (error) {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to delete sub-admin",
        });
      }
    }
  };

  // Open roles dialog
  const openRolesDialog = (subAdminData) => {
    // Normalize current roles to identifiers in case API returns names
    const normalize = (val) => (typeof val === 'string' ? val.trim() : val);
    const currentRoleIds = (subAdminData.roles || [])
      .map((roleValue) => {
        const v = normalize(roleValue);
        const match = roles.find((r) => {
          const id = normalize(r.identifier);
          const en = normalize(r.displayName_En);
          const ar = normalize(r.displayName_Ar);
          return (
            id === v ||
            (typeof v === 'string' && (en?.toLowerCase() === v.toLowerCase() || ar?.toLowerCase() === v.toLowerCase()))
          );
        });
        return match?.identifier || v; // prefer identifier
      })
      .filter(Boolean);

    setRolesData({
      uuid: subAdminData.uuid,
      roles: currentRoleIds,
    });
    setRolesDialogVisible(true);
  };

  // Open password dialog
  const openPasswordDialog = (subAdminData) => {
    setPasswordData({
      uuid: subAdminData.uuid,
      newPassword: "",
      confirmNewPassword: "",
    });
    setPasswordDialogVisible(true);
  };

  // Reset form
  const resetForm = () => {
    setFormData({
      uuid: null,
      fullName: "",
      phoneNumber: "",
      password: "",
      confirmPassword: "",
      state: "Enabled",
      roles: [],
    });
  };

  // Action buttons template
  const actionTemplate = (rowData) => (
    <div className="action-table">
      <PermissionGate permission="p_sa0003">
        <a
          className="edit"
          onClick={() => openRolesDialog(rowData)}
          title="Manage Roles"
        >
          <i className="fa fa-user-tag"></i>
        </a>
        <a
          className="edit"
          onClick={() => openPasswordDialog(rowData)}
          title="Change Password"
        >
          <i className="fa fa-key"></i>
        </a>
      </PermissionGate>
      <PermissionGate permission="p_sa0004">
        <a
          className="delete"
          onClick={() => deleteSubAdmin(rowData.uuid)}
          title="Delete"
        >
          <i className="fa fa-trash"></i>
        </a>
      </PermissionGate>
    </div>
  );

  // Status template
  const statusTemplate = (rowData) => (
    <span className={`status-badge ${rowData.state?.toLowerCase()}`}>
      {rowData.state}
    </span>
  );

  // Roles template
  const rolesTemplate = (rowData) => (
    <div className="roles-display">
      {rowData.roles?.map((role, index) => (
        <span key={index} className="role-chip">
          {role}
        </span>
      ))}
    </div>
  );

  // Global filter
  const onGlobalFilterChange = (e) => {
    const value = e.target.value;
    setGlobalFilterValue(value);

    if (value === "") {
      setSubAdmins(subAdmins);
    } else {
      const filtered = subAdmins.filter(
        (subAdmin) =>
          subAdmin.fullName?.toLowerCase().includes(value.toLowerCase()) ||
          subAdmin.phoneNumber?.toLowerCase().includes(value.toLowerCase()) ||
          subAdmin.state?.toLowerCase().includes(value.toLowerCase())
      );
      setSubAdmins(filtered);
    }
  };

  useEffect(() => {
    getSubAdmins();
    getRoles();
  }, []);

  const roleOptions = (roles?.map((role) => ({ label: role.displayName_En, value: role.identifier })) || []);

  return (
    <div className="main">
      <div className="sidebar">
        <Sidebar />
      </div>
      <div className="dev-table">
        <Navbar />
        <div className="table-card">
          <div className="table">
            <PermissionGate permission="p_sa0002">
              <a
                className="add"
                onClick={() => {
                  setVisible(true);
                }}
              >
                <i className="fa fa-plus"></i>
              </a>
            </PermissionGate>

            <div className="table-header">
              <h2>Sub-Admins Management</h2>
              <div className="search-box">
                <InputText
                  value={globalFilterValue}
                  onChange={onGlobalFilterChange}
                  placeholder="Search sub-admins..."
                  className="search-input"
                />
              </div>
            </div>

            <DataTable
              value={subAdmins}
              paginator
              rows={10}
              loading={loading}
              globalFilter={globalFilterValue}
              emptyMessage="No sub-admins found."
              className="sub-admins-table"
            >
              <Column field="fullName" header="Full Name" sortable />
              <Column field="phoneNumber" header="Phone Number" sortable />
              <Column
                field="state"
                header="State"
                body={statusTemplate}
                sortable
              />
              <Column field="roles" header="Roles" body={rolesTemplate} />
              <Column field="dateCreated" header="Date Created" sortable />
              <Column field="dateLastLogin" header="Last Login" sortable />
              <Column header="Actions" body={actionTemplate} />
            </DataTable>

            {/* Add Sub-Admin Dialog */}
            <Dialog
              header="Add New Sub-Admin"
              visible={visible}
              style={{ width: "600px", maxWidth: "90vw" }}
              onHide={() => {
                setVisible(false);
                resetForm();
              }}
              className="subadmin-dialog"
            >
              <div className="form-container" style={{ padding: '0' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Full Name</label>
                    <InputText
                      value={formData.fullName}
                      onChange={(e) =>
                        setFormData({ ...formData, fullName: e.target.value })
                      }
                      placeholder="Enter full name"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Phone Number</label>
                    <InputText
                      value={formData.phoneNumber}
                      onChange={(e) =>
                        setFormData({ ...formData, phoneNumber: e.target.value })
                      }
                      placeholder="Enter phone number"
                      className="form-input"
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <Password
                      value={formData.password}
                      onChange={(e) =>
                        setFormData({ ...formData, password: e.target.value })
                      }
                      placeholder="Enter password"
                      className="form-input"
                      toggleMask
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Confirm Password</label>
                    <Password
                      value={formData.confirmPassword}
                      onChange={(e) =>
                        setFormData({
                          ...formData,
                          confirmPassword: e.target.value,
                        })
                      }
                      placeholder="Confirm password"
                      className="form-input"
                      toggleMask
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">State</label>
                    <select
                      value={formData.state}
                      onChange={(e) => setFormData({ ...formData, state: e.target.value })}
                      className="form-input"
                    >
                      <option value="" disabled>Select state</option>
                      <option value="Enabled">Enabled</option>
                      <option value="Disabled">Disabled</option>
                    </select>
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group roles-group">
                    <label className="form-label">Roles</label>
                    <MultiSelectDropdown
                      value={formData.roles || []}
                      options={roleOptions}
                      onChange={(vals) =>
                        setFormData({ ...formData, roles: vals || [] })
                      }
                      placeholder="Select roles"
                      className="roles-multiselect"
                      maxSelectedLabels={2}
                      showSelectAll={true}
                      selectAllLabel="Select All Roles"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <Button
                    label="Create Sub-Admin"
                    onClick={createSubAdmin}
                    className="submit-btn"
                  />
                  <Button
                    label="Cancel"
                    onClick={() => {
                      setVisible(false);
                      resetForm();
                    }}
                    className="cancel-btn"
                    outlined
                  />
                </div>
              </div>
            </Dialog>

            {/* Change Password Dialog */}
            <Dialog
              header="Change Password"
              visible={passwordDialogVisible}
              style={{ width: "500px", maxWidth: "90vw" }}
              onHide={() => {
                setPasswordDialogVisible(false);
                setPasswordData({
                  uuid: null,
                  newPassword: "",
                  confirmNewPassword: "",
                });
              }}
              className="subadmin-dialog"
            >
              <div className="form-container" style={{ padding: '0' }}>
                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">New Password</label>
                    <Password
                      value={passwordData.newPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          newPassword: e.target.value,
                        })
                      }
                      placeholder="Enter new password"
                      className="form-input"
                      toggleMask
                    />
                  </div>
                </div>

                <div className="form-row">
                  <div className="form-group">
                    <label className="form-label">Confirm New Password</label>
                    <Password
                      value={passwordData.confirmNewPassword}
                      onChange={(e) =>
                        setPasswordData({
                          ...passwordData,
                          confirmNewPassword: e.target.value,
                        })
                      }
                      placeholder="Confirm new password"
                      className="form-input"
                      toggleMask
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <Button
                    label="Change Password"
                    onClick={changePassword}
                    className="submit-btn"
                  />
                  <Button
                    label="Cancel"
                    onClick={() => {
                      setPasswordDialogVisible(false);
                      setPasswordData({
                        uuid: null,
                        newPassword: "",
                        confirmNewPassword: "",
                      });
                    }}
                    className="cancel-btn"
                    outlined
                  />
                </div>
              </div>
            </Dialog>

            {/* Manage Roles Dialog */}
            <Dialog
              header="Manage Roles"
              visible={rolesDialogVisible}
              style={{ width: "500px", maxWidth: "90vw" }}
              onHide={() => {
                setRolesDialogVisible(false);
                setRolesData({ uuid: null, roles: [] });
              }}
              className="subadmin-dialog"
            >
              <div className="form-container" style={{ padding: '0' }}>
                <div className="form-row">
                  <div className="form-group roles-group">
                    <label className="form-label">Assign Roles</label>
                    <MultiSelectDropdown
                      value={rolesData.roles || []}
                      options={roleOptions}
                      onChange={(vals) =>
                        setRolesData({ ...rolesData, roles: vals || [] })
                      }
                      placeholder="Select roles"
                      className="roles-multiselect"
                      maxSelectedLabels={2}
                      showSelectAll={true}
                      selectAllLabel="Select All Roles"
                    />
                  </div>
                </div>

                <div className="form-actions">
                  <Button
                    label="Update Roles"
                    onClick={updateSubAdminRoles}
                    className="submit-btn"
                  />
                  <Button
                    label="Cancel"
                    onClick={() => {
                      setRolesDialogVisible(false);
                      setRolesData({ uuid: null, roles: [] });
                    }}
                    className="cancel-btn"
                    outlined
                  />
                </div>
              </div>
            </Dialog>
          </div>
        </div>
      </div>
    </div>
  );
}

export default SubAdmins;
